/**
 * Authenticated SSE client using fetch + ReadableStream.
 *
 * EventSource doesn't support custom headers (needed for auth).
 * This uses fetch() to open a streaming connection and parses
 * SSE event/data lines manually.
 */

export interface SSEEvent {
  event: string;
  data: string;
}

/**
 * Async generator that yields parsed SSE events from a streaming response.
 *
 * @param url - The SSE endpoint URL
 * @param headers - Request headers (including Authorization)
 * @param signal - AbortSignal for cancellation
 */
export async function* streamSSE(
  url: string,
  headers: Record<string, string>,
  signal?: AbortSignal,
): AsyncGenerator<SSEEvent> {
  const res = await fetch(url, {
    headers: {
      ...headers,
      Accept: "text/event-stream",
    },
    signal,
  });

  if (!res.ok) {
    throw new Error(`SSE connection failed: ${res.status} ${res.statusText}`);
  }

  const reader = res.body!.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let currentEvent = "";
  let currentData = "";

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      // Process complete lines
      const lines = buffer.split("\n");
      // Keep the last incomplete line in the buffer
      buffer = lines.pop() ?? "";

      for (const line of lines) {
        if (line.startsWith("event: ")) {
          currentEvent = line.slice(7).trim();
        } else if (line.startsWith("data: ")) {
          currentData = line.slice(6).trim();
        } else if (line.startsWith(":")) {
          // Comment line (keepalive) — ignore
          continue;
        } else if (line === "") {
          // Empty line = end of event
          if (currentEvent || currentData) {
            yield {
              event: currentEvent || "message",
              data: currentData,
            };
            currentEvent = "";
            currentData = "";
          }
        }
      }
    }

    // Flush any remaining event
    if (currentEvent || currentData) {
      yield {
        event: currentEvent || "message",
        data: currentData,
      };
    }
  } finally {
    reader.releaseLock();
  }
}
